import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { requestPasswordReset } from '../api/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, Lock, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [envoiLienEnCours, setEnvoiLienEnCours] = useState(false);

  // Le lien part vers l'adresse saisie dans le formulaire : inutile de la
  // redemander dans un second écran.
  const demanderReinitialisation = async () => {
    const email = formData.email?.trim();
    if (!email) {
      toast.error("Saisissez d'abord votre adresse email ci-dessus.");
      return;
    }

    setEnvoiLienEnCours(true);
    try {
      await requestPasswordReset(email);
      toast.success(
        'Si ce compte existe, un lien de réinitialisation vient d\'être envoyé. Il est valable 30 minutes.',
        { duration: 7000 }
      );
    } catch (error) {
      toast.error(
        error.response?.status === 429
          ? 'Trop de demandes. Réessayez dans une heure.'
          : "L'envoi a échoué. Réessayez plus tard."
      );
    } finally {
      setEnvoiLienEnCours(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success('Connexion réussie !');
      navigate('/admin/dashboard');
    } else {
      toast.error(result.error || 'Identifiants incorrects');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center space-x-2 mb-4">
            <div className="bg-blue-600 p-3 rounded-xl">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">NETTO+ Admin</h1>
          <p className="text-gray-600">Connectez-vous pour gérer votre site</p>
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-blue-600 text-white rounded-t-xl">
            <CardTitle className="flex items-center space-x-2">
              <Lock className="w-5 h-5" />
              <span>Connexion Admin</span>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-semibold">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@nettoplus.be"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="pl-10 h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-semibold">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    className="pl-10 h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg shadow-blue-600/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-600/40 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connexion...
                  </span>
                ) : (
                  'Se connecter'
                )}
              </Button>

              <button
                type="button"
                onClick={demanderReinitialisation}
                disabled={envoiLienEnCours}
                className="w-full text-sm text-gray-500 hover:text-blue-600 transition-colors disabled:opacity-50"
              >
                {envoiLienEnCours ? 'Envoi du lien…' : 'Mot de passe oublié ?'}
              </button>

              <p className="text-sm text-gray-400 text-center">
                Connexion sécurisée
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Back to site */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            ← Retour au site
          </button>
        </div>
      </div>
    </div>
  );
};
